import { useState, useRef, useCallback } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Checkbox,
  useToast,
} from '@lead-magnifier/ui'
import { Upload, AlertTriangle, CheckCircle2, Mail, FileText, X, Loader2 } from 'lucide-react'
import { useBulkInvite } from '@/hooks/use-team'

interface CsvRow {
  firma: string
  ansprechpartner: string
  email: string
  valid: boolean
  selected: boolean
}

function parseCSV(text: string): CsvRow[] {
  const lines = text.trim().split('\n')
  if (lines.length < 2) return []

  // Header erkennen
  const headerLine = lines[0].toLowerCase()
  const separator = headerLine.includes(';') ? ';' : ','
  const headers = lines[0].split(separator).map((h) => h.trim().toLowerCase().replace(/"/g, ''))

  // Spalten-Mapping (flexibel)
  const firmaIdx = headers.findIndex((h) =>
    ['firma', 'firmenname', 'company', 'unternehmen', 'organisation', 'name'].includes(h)
  )
  const emailIdx = headers.findIndex((h) =>
    ['email', 'e-mail', 'mail', 'e-mail-adresse', 'emailadresse'].includes(h)
  )
  const kontaktIdx = headers.findIndex((h) =>
    ['ansprechpartner', 'kontakt', 'kontaktperson', 'contact', 'vorname', 'person'].includes(h)
  )

  const rows: CsvRow[] = []

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue

    // Einfaches CSV-Parsing (unterstützt quoted fields)
    const cols = parseCsvLine(line, separator)

    const email = emailIdx >= 0 ? cols[emailIdx]?.trim().replace(/"/g, '') || '' : ''
    const firma = firmaIdx >= 0 ? cols[firmaIdx]?.trim().replace(/"/g, '') || '' : ''
    const ansprechpartner = kontaktIdx >= 0 ? cols[kontaktIdx]?.trim().replace(/"/g, '') || '' : ''

    // Mindestens Firma oder E-Mail muss da sein
    if (!firma && !email) continue

    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

    rows.push({
      firma,
      ansprechpartner,
      email,
      valid,
      selected: valid, // Nur gültige sind vorausgewählt
    })
  }

  return rows
}

function parseCsvLine(line: string, separator: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === separator && !inQuotes) {
      result.push(current)
      current = ''
    } else {
      current += char
    }
  }
  result.push(current)
  return result
}

export function BulkInviteDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [step, setStep] = useState<'upload' | 'preview' | 'done'>('upload')
  const [rows, setRows] = useState<CsvRow[]>([])
  const [customText, setCustomText] = useState('')
  const [results, setResults] = useState<{ success: number; failed: number }>({ success: 0, failed: 0 })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const bulkInvite = useBulkInvite()
  const { toast } = useToast()

  const reset = useCallback(() => {
    setStep('upload')
    setRows([])
    setCustomText('')
    setResults({ success: 0, failed: 0 })
  }, [])

  const handleClose = (open: boolean) => {
    if (!open) reset()
    onOpenChange(open)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target?.result as string
      const parsed = parseCSV(text)

      if (parsed.length === 0) {
        toast({
          title: 'Keine Daten gefunden',
          description: 'Die CSV-Datei enthält keine verwertbaren Zeilen. Erwartete Spalten: Firma, E-Mail, Ansprechpartner',
          variant: 'destructive',
        })
        return
      }

      setRows(parsed)
      setStep('preview')
    }
    reader.readAsText(file, 'utf-8')

    // Input zurücksetzen, damit gleiche Datei erneut gewählt werden kann
    e.target.value = ''
  }

  const toggleRow = (index: number) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, selected: !r.selected } : r))
    )
  }

  const toggleAll = () => {
    const validRows = rows.filter((r) => r.valid)
    const allSelected = validRows.every((r) => r.selected)
    setRows((prev) =>
      prev.map((r) => (r.valid ? { ...r, selected: !allSelected } : r))
    )
  }

  const selectedCount = rows.filter((r) => r.selected && r.valid).length
  const invalidCount = rows.filter((r) => !r.valid).length

  const handleInviteAll = async () => {
    const toInvite = rows.filter((r) => r.selected && r.valid)
    if (toInvite.length === 0) return

    try {
      const result = await bulkInvite.mutateAsync({
        invites: toInvite.map((r) => ({
          email: r.email,
          firma: r.firma,
          ansprechpartner: r.ansprechpartner,
        })),
        customText,
      })

      setResults(result)
      setStep('done')
    } catch (err) {
      toast({
        title: 'Fehler beim Einladen',
        description: err instanceof Error ? err.message : 'Einladungen konnten nicht erstellt werden',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        {step === 'upload' && (
          <>
            <DialogHeader>
              <DialogTitle>Aussteller per CSV einladen</DialogTitle>
              <DialogDescription>
                Lade eine CSV-Datei mit deiner Firmenliste hoch. Erwartete Spalten: Firma, E-Mail, Ansprechpartner (optional).
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 py-4">
              <div
                className="border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
                <p className="font-medium">CSV-Datei auswählen</p>
                <p className="text-sm text-muted-foreground mt-1">
                  .csv mit Semikolon oder Komma als Trennzeichen
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>

              <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium mb-2">Beispiel-Format:</p>
                <pre className="text-xs text-muted-foreground font-mono">
{`Firma;E-Mail;Ansprechpartner
Holzbau Müller GmbH;office@holz-mueller.at;Thomas Müller
Elektro Huber;info@elektro-huber.at;Maria Huber
Tischlerei Berger;;
Maier & Co KG;kontakt@maier.at;`}
                </pre>
              </div>
            </div>
          </>
        )}

        {step === 'preview' && (
          <>
            <DialogHeader>
              <DialogTitle>Vorschau — {rows.length} Einträge</DialogTitle>
              <DialogDescription>
                {invalidCount > 0 ? (
                  <span className="text-orange-600">
                    {invalidCount} {invalidCount === 1 ? 'Eintrag hat keine' : 'Einträge haben keine'} gültige E-Mail-Adresse und {invalidCount === 1 ? 'kann' : 'können'} nicht eingeladen werden.
                  </span>
                ) : (
                  'Alle Einträge haben eine gültige E-Mail-Adresse.'
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-auto min-h-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={rows.filter((r) => r.valid).every((r) => r.selected)}
                        onCheckedChange={toggleAll}
                      />
                    </TableHead>
                    <TableHead>Firma</TableHead>
                    <TableHead>Ansprechpartner</TableHead>
                    <TableHead>E-Mail</TableHead>
                    <TableHead className="w-[80px]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, i) => (
                    <TableRow key={i} className={!row.valid ? 'opacity-60' : ''}>
                      <TableCell>
                        <Checkbox
                          checked={row.selected}
                          disabled={!row.valid}
                          onCheckedChange={() => toggleRow(i)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{row.firma || '—'}</TableCell>
                      <TableCell>{row.ansprechpartner || '—'}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {row.email || <span className="text-muted-foreground italic">fehlt</span>}
                      </TableCell>
                      <TableCell>
                        {row.valid ? (
                          <Badge variant="outline" className="text-green-600 border-green-200">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            OK
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-orange-600 border-orange-200">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Fehlt
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="space-y-3 pt-4 border-t">
              <div className="space-y-2">
                <Label htmlFor="customText">Persönlicher Text für die Einladung (optional)</Label>
                <Textarea
                  id="customText"
                  placeholder="z.B. Wir freuen uns, Sie als Aussteller bei der Herbstmesse 2026 begrüßen zu dürfen. Mit diesem Tool können Sie Ihre Leads direkt am Stand erfassen."
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter className="flex-row justify-between sm:justify-between">
              <Button variant="outline" onClick={reset}>
                <X className="h-4 w-4 mr-2" />
                Andere Datei
              </Button>
              <Button
                onClick={handleInviteAll}
                disabled={selectedCount === 0 || bulkInvite.isPending}
              >
                {bulkInvite.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Wird eingeladen...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    {selectedCount} {selectedCount === 1 ? 'Aussteller' : 'Aussteller'} einladen
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'done' && (
          <>
            <DialogHeader>
              <DialogTitle>Einladungen erstellt</DialogTitle>
            </DialogHeader>

            <div className="py-8 text-center space-y-4">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <p className="text-lg font-semibold">
                  {results.success} {results.success === 1 ? 'Einladung' : 'Einladungen'} erfolgreich erstellt
                </p>
                {results.failed > 0 && (
                  <p className="text-sm text-orange-600 mt-1">
                    {results.failed} konnten nicht erstellt werden (bereits eingeladen oder Mitglied)
                  </p>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Die Einladungslinks findest du in der Übersicht unter „Offene Einladungen".
                Du kannst die Links dort kopieren und per E-Mail versenden.
              </p>
            </div>

            <DialogFooter>
              <Button onClick={() => handleClose(false)}>
                Fertig
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
